import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Text,
  Flex,
  Spinner,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  HStack,
  Box,
  Badge,
  VStack,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Icon,
  Button,
  TableContainer,
  Checkbox,
  Stack,
  IconButton,
} from '@chakra-ui/react';
import CsvDownloader from 'react-csv-downloader';
import { currentDatabaseSelector } from '@transport-insights/uikit';
import {
  Map,
  AdvancedMarker,
  InfoWindow,
  useMapsLibrary,
  useAdvancedMarkerRef,
} from '@vis.gl/react-google-maps';
import { useEffect, useState } from 'react';
// import { geocodeByAddress } from 'react-google-places-autocomplete'; TODO: Remove if not needed
import { useRecoilValue } from 'recoil';
import { useGetTtmSiteInspectionList } from '@src/modules/performance/context/temporary-traffic-management-api-hooks';
import EmptyState from '@src/components/shared/EmptyState';
import DateRangePicker from '@src/modules/performance/components/DateRangePicker';
import { TRAFFIC_LIGHT_COLOURS, TTM_SITE_STATUSES } from '@src/modules/performance/shared/constants';
import IndicatorLegend from '@src/modules/performance/components/IndicatorLegend';
import {
  FiCalendar, FiDownloadCloud, FiUser,
  FiX,
} from 'react-icons/fi';
import { useConfig } from '@src/context/config';

function convertToLatLngLiteral(latLngString) {
  if (!latLngString) return null;
  const [lat, lng] = latLngString.split(',').map(Number);
  return { lat, lng };
}

const STATUS_COLORS = {
  1: TRAFFIC_LIGHT_COLOURS.GREY,
  2: TRAFFIC_LIGHT_COLOURS.GREEN,
  3: TRAFFIC_LIGHT_COLOURS.ORANGE,
  4: TRAFFIC_LIGHT_COLOURS.RED,
};

function insertLineBreaks(str, wordsPerLine) {
  const words = str.split(' ');
  let result = '';

  for (let i = 0; i < words.length; i += wordsPerLine) {
    result += `${words.slice(i, i + wordsPerLine).join(' ')}\n`;
  }

  return result;
}

function AdvancedMarkerWithRef({ site, siteLocation }) {
  const [markerRef, marker] = useAdvancedMarkerRef();
  const [isVisible, setIsVisible] = useState(false);
  const handleClick = () => {
    setIsVisible(!isVisible);
  };
  return (
    <AdvancedMarker
      ref={markerRef}
      position={convertToLatLngLiteral(site.location)}
      title={site.address}
    >
      <Box
        width={5}
        height={5}
        background={STATUS_COLORS[site.siteStatus]}
        borderRadius="full"
        border="2px solid white"
        boxShadow="md"
        cursor="pointer"
        onClick={handleClick}
        _hover={{ boxShadow: 'lg', transform: 'scale(1.2)' }}
      />

      {isVisible && (
      <InfoWindow
        position={siteLocation}
        anchor={marker}
      >
        <Box>
          <IconButton
            icon={<Icon as={FiX} color="gray.500" boxSize={4} />}
            onClick={handleClick}
            position="absolute"
            top="8px"
            right="8px"
            size="sm"
            backgroundColor="white"
            zIndex="1"
            colorScheme="gray"
            tabIndex={-1}
          />
          <VStack spacing={2} align="flex-start">
            <Badge backgroundColor={STATUS_COLORS[site.siteStatus]} color="white">
              {TTM_SITE_STATUSES.find((x) => x.id.toString() === site.siteStatus)?.label}
            </Badge>
            <Text fontSize="md" fontWeight="bold" whiteSpace="pre-line">
              {site.address}
            </Text>
            {site.description && (
            <Text fontSize="sm" whiteSpace="pre-line">{site.description}</Text>
            )}
            <HStack align="center">
              <Icon as={FiCalendar} boxSize={4} />
              <Text fontSize="sm">{new Date(site.inspectionDate).toLocaleDateString('en-NZ')}</Text>
            </HStack>
            <HStack align="center">
              <Icon as={FiUser} boxSize={4} />
              <Text fontSize="sm">{site.userName}</Text>
            </HStack>
          </VStack>
        </Box>
      </InfoWindow>
      )}
    </AdvancedMarker>
  );
}

function TtmSiteInspectionView({ isOpen, onClose }) {
  const currentDatabase = useRecoilValue(currentDatabaseSelector);
  const [rcaLocation, setRcaLocation] = useState(null);
  const [error, setError] = useState(null);
  const config = useConfig();

  const addressLookup = currentDatabase?.name === 'State Highways'
    ? 'New Zealand'
    : `${currentDatabase?.name?.replace('Council', '').replace('Transport', '').replace('City', '').trim()}, New Zealand`;
  const zoomLevel = currentDatabase?.name.includes('City') ? 11 : 9;

  const [status, setStatus] = useState([...TTM_SITE_STATUSES.map((siteStatus) => siteStatus.id.toString())]);
  const [dateRange, setDateRange] = useState(null);
  const { data, isLoading, error: dataError } = useGetTtmSiteInspectionList({ ...dateRange });
  const [filteredData, setFilteredData] = useState([]);
  const statusCounts = data?.reduce((acc, site) => {
    const statusLabel = TTM_SITE_STATUSES.find((x) => x.id.toString() === site.siteStatus)?.label;
    if (!statusLabel) return acc;
    acc[statusLabel] = (acc[statusLabel] || 0) + 1;
    return acc;
  }, {});

  const csvData = filteredData?.map((site) => ({
    RCA: currentDatabase?.name,
    Status: `"${TTM_SITE_STATUSES.find((x) => x.id.toString() === site.siteStatus)?.label}"`,
    Address: `"${site.address}"`,
    Description: `"${site.description}"`,
    GPS: `"${site.location}"`,
    DateInspected: `"${new Date(site.inspectionDate).toLocaleDateString('en-NZ')}"`,
    InspectedBy: `"${site.userName}"`,
  }));

  // Load geocoding library
  const geocodingLibrary = useMapsLibrary('geocoding');

  // Geocode the RCA address once library is ready
  useEffect(() => {
    if (!geocodingLibrary || !addressLookup) {
      return;
    }
    (async () => {
      try {
        const geocoder = new geocodingLibrary.Geocoder();
        const { results } = await geocoder.geocode({ address: addressLookup });
        if (results && results.length > 0) {
          const loc = results[0].geometry.location;
          setRcaLocation({ lat: loc.lat(), lng: loc.lng() });
        } else {
          setError('Location not found');
        }
      } catch (err) {
        console.error('Geocode error', err);
        setError('Failed to geocode address');
      }
    })();
  }, [geocodingLibrary, addressLookup]);

  // Update filtered data when status or data changes
  useEffect(() => {
    if (data) {
      const filtered = data.filter((site) => status.length === 0 || status.includes(site.siteStatus));
      setFilteredData(filtered);
    }
  }, [data, status]);

  const handleDateChange = (dates) => {
    setDateRange(dates);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="full">
      <ModalOverlay />
      <ModalContent height="100vh">
        <ModalHeader>
          Site Inspections for
          {' '}
          {currentDatabase?.name}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody display="flex" flexDirection="column" flex="1" pb={6}>
          <Stack direction={{ base: 'column', lg: 'row' }} mb={6} width="100%" justify="space-between">
            <Stack
              direction={{ base: 'column', md: 'row' }}
              align={{ base: 'flex-start', md: 'center' }}
              spacing={6}
              background="gray.50"
              p={4}
              borderRadius="base"
              width={{ base: '100%', lg: 'fit-content' }}
              height="fit-content"
            >
              <DateRangePicker onDateChange={handleDateChange} />
              <Stack direction={{ base: 'column', md: 'row' }}>
                {TTM_SITE_STATUSES.map((siteStatus) => (
                  <Checkbox
                    key={siteStatus.id}
                    size="sm"
                    isChecked={status.includes(siteStatus.id.toString())}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setStatus((prev) => [...prev, siteStatus.id.toString()]);
                      } else {
                        setStatus((prev) => prev.filter((id) => id !== siteStatus.id.toString()));
                      }
                    }}
                  >
                    {siteStatus.label}
                  </Checkbox>
                ))}
              </Stack>
            </Stack>
            <Stack direction="row" spacing={6} align="flex-start">
              <VStack>
                <Text fontSize="2xl" fontWeight="bold">{data?.length || 0}</Text>
                <Text fontSize="xs">Total Inspections</Text>
              </VStack>
              {statusCounts && Object.keys(statusCounts).map((statusLabel) => (
                <VStack key={statusLabel}>
                  <Text fontSize="2xl" fontWeight="bold">{statusCounts[statusLabel]}</Text>
                  <Text
                    fontSize="xs"
                    whiteSpace="pre-line"
                  >
                    {insertLineBreaks(statusLabel, 2)}
                  </Text>
                </VStack>
              ))}
            </Stack>

          </Stack>
          {isLoading && (
            <Flex justify="center" align="center" flex="1">
              <Spinner />
              <Text ml={4} fontSize="sm">Loading Site Inspections</Text>
            </Flex>
          )}
          {/* Map / List view when we have location, data, no errors */}
          {rcaLocation
            && !isLoading
            && !error
            && !dataError
            && filteredData.length > 0 && (
            <Tabs
              variant="soft-rounded"
              colorScheme="brand.orange"
              display="flex"
              flex="1"
              flexDir="column"
            >
              <TabList
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                width="100%"
              >
                <HStack spacing={2} flex="1">
                  <Tab>
                    Map View
                  </Tab>
                  <Tab>
                    List View
                  </Tab>
                </HStack>
                <CsvDownloader
                  filename={`${currentDatabase?.name} - Site Inspections (${dateRange?.startDate} to ${dateRange?.endDate})`}
                  extension=".csv"
                  separator=","
                  datas={csvData}
                >
                  <Button
                    onClick={() => {}}
                    leftIcon={<Icon as={FiDownloadCloud} boxSize={4} />}
                    size="sm"
                    variant="ghost"
                    isDisabled={filteredData?.length === 0}
                  >
                    Download CSV
                  </Button>
                </CsvDownloader>
              </TabList>
              <TabPanels display="flex" flex="1">
                <TabPanel display="flex" flex="1" p={0} pt={4}>
                  <Map
                    defaultCenter={rcaLocation}
                    defaultZoom={zoomLevel}
                    options={{
                      disableDefaultUI: true,
                      zoomControl: true,
                      mapTypeControl: false,
                      fullscreenControl: false,
                      draggable: true,
                      scrollwheel: true,
                      clickableIcons: false,
                    }}
                    mapId={config?.GOOGLE_MAP_LOCATIONS_ID}
                  >
                    <Box
                      position="absolute"
                      right={4}
                      top={4}
                      background="white"
                      boxShadow="base"
                      borderRadius="base"
                      p={4}
                    >
                      <IndicatorLegend items={TTM_SITE_STATUSES.map((siteStatus) => ({ label: siteStatus.label, color: STATUS_COLORS[siteStatus.id] }))} />
                    </Box>
                    {filteredData?.map((site) => {
                      const siteLocation = convertToLatLngLiteral(site.location);
                      return (
                        siteLocation && (
                          <AdvancedMarkerWithRef
                            key={site.id}
                            site={site}
                            siteLocation={siteLocation}
                          />
                        )
                      );
                    })}
                  </Map>
                </TabPanel>
                <TabPanel width="100%" p={0} pt={4}>
                  <TableContainer
                    p={4}
                    width="100%"
                    border="1px solid"
                    borderColor="gray.100"
                    borderRadius="md"
                  >
                    <Table variant="simple" size="sm">
                      <Thead>
                        <Tr>
                          <Th>Status</Th>
                          <Th>Address</Th>
                          <Th>Description</Th>
                          <Th>GPS Location</Th>
                          <Th>Date Inspected</Th>
                          <Th>Inspected By</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {filteredData?.map((site) => (
                          <Tr key={site.id}>
                            <Td>
                              <Badge backgroundColor={STATUS_COLORS[site.siteStatus]} color="white">
                                {TTM_SITE_STATUSES.find((x) => x.id.toString() === site.siteStatus)?.label}
                              </Badge>
                            </Td>
                            <Td>{site.address}</Td>
                            <Td>{site.description}</Td>
                            <Td>{site.location}</Td>
                            <Td>{new Date(site.inspectionDate).toLocaleDateString('en-NZ')}</Td>
                            <Td>{site.userName}</Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </TableContainer>
                </TabPanel>
              </TabPanels>
            </Tabs>
          )}
          {(error || dataError) && (
            <EmptyState
              title="Error Loading Inspections"
              message={(
                <Text color="gray.400">There was an error loading the site inspections. Please check later.</Text>
              )}
            />
          )}
          {(!filteredData || filteredData?.length === 0) && !isLoading && !error && !dataError && (
            <EmptyState
              title="No Site Inspections"
              message={(
                <Text color="gray.400">There were no site inspections found with the selected filters.</Text>
              )}
            />
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}

export default TtmSiteInspectionView;
